// Órdenes de Trabajo (producción interna y maquila).
// Producción → Fabricación → Órdenes de Trabajo.
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, DollarSign, Eye, Loader2, PackageCheck, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { ExportarBotones } from '@/lib/utils/exportarTabla';
import {
  Campo, Location, Modal, PartnerOpt, Producto, UbicacionOpt, fieldCls,
  locationsList, money, num, partnersList, toNum, ubicacionesList, inv,
} from '@/features/inventario/shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { ESTADO_CLS, ESTADO_LABEL, OtDetalle, OtResumen, invalidateProduccion, prodRpc } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

interface VarianteOpt { id: string; codigo: string; nombre: string }

export function OrdenesTrabajo() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('');
  const [nuevaAbierta, setNuevaAbierta] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);

  const { data: ordenes = [], isFetching } = useQuery<OtResumen[]>({
    queryKey: ['prod-ot-lista', estado],
    queryFn: () => prodRpc<OtResumen[]>('ot_listar', { _estado: estado || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const invalidar = () => invalidateProduccion(qc);

  const getVal = useCallback((r: OtResumen, key: string): string | number | null => {
    switch (key) {
      case 'codigo': return r.codigo;
      case 'fecha': return r.fecha;
      case 'tipo': return r.tipo;
      case 'estado': return r.estado;
      case 'producto': return r.producto_padre;
      case 'plan': return Number(r.unidades_plan);
      case 'producido': return Number(r.unidades_producidas);
      case 'costo': return Number(r.costo_mp) + Number(r.costo_mo) + Number(r.costo_cif) + Number(r.costo_maquila);
      default: return null;
    }
  }, []);
  const sort = useSort(ordenes, getVal);

  const totalCosto = (r: OtResumen) =>
    Number(r.costo_mp) + Number(r.costo_mo) + Number(r.costo_cif) + Number(r.costo_maquila);

  const exportFilas = useMemo(() => {
    const filas = sort.sorted.map((r, i) => [
      i + 1, r.codigo, r.fecha, r.tipo, ESTADO_LABEL[r.estado] ?? r.estado, r.producto_padre,
      r.maquilador ?? '', Number(r.unidades_plan), Number(r.unidades_producidas), totalCosto(r),
    ]);
    if (filas.length) {
      filas.push(['', 'Totales', '', '', '', '', '',
        sort.sorted.reduce((a, r) => a + Number(r.unidades_plan), 0),
        sort.sorted.reduce((a, r) => a + Number(r.unidades_producidas), 0),
        sort.sorted.reduce((a, r) => a + totalCosto(r), 0)]);
    }
    return filas;
  }, [sort.sorted]);

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Órdenes de Trabajo</CardTitle>
          <div className="flex items-center gap-2">
            <select className={`${fieldCls} w-44`} value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos los estados</option>
              {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <ExportarBotones nombre="ordenes_trabajo" titulo="Órdenes de Trabajo"
              headers={['N°', 'Código', 'Fecha', 'Tipo', 'Estado', 'Modelo', 'Maquilador', 'Plan', 'Producido', 'Costo total']}
              filas={exportFilas} colsDerecha={[0, 7, 8, 9]} disabled={!ordenes.length} />
            <Button onClick={() => setNuevaAbierta(true)}><Plus className="h-4 w-4 mr-1" /> Nueva OT</Button>
          </div>
        </CardHeader>
        <CardContent>
          {isFetching && !ordenes.length ? (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="codigo" sort={sort}>Código</SortTh>
                    <SortTh col="fecha" sort={sort}>Fecha</SortTh>
                    <SortTh col="tipo" sort={sort}>Tipo</SortTh>
                    <SortTh col="estado" sort={sort}>Estado</SortTh>
                    <SortTh col="producto" sort={sort}>Modelo</SortTh>
                    <SortTh col="plan" sort={sort} align="right">Plan</SortTh>
                    <SortTh col="producido" sort={sort} align="right">Producido</SortTh>
                    <SortTh col="costo" sort={sort} align="right">Costo total</SortTh>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {sort.sorted.map((r, i) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                      <td className="py-2 pr-3">{r.fecha}</td>
                      <td className="py-2 pr-3 capitalize">{r.tipo}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_CLS[r.estado] ?? ''}`}>
                          {ESTADO_LABEL[r.estado] ?? r.estado}
                        </span>
                      </td>
                      <td className="py-2 pr-3">{r.producto_padre}{r.maquilador ? <span className="text-xs text-muted-foreground"> · {r.maquilador}</span> : null}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(r.unidades_plan, 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(r.unidades_producidas, 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totalCosto(r))}</td>
                      <td className="py-2 pr-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDetalleId(r.id)} title="Ver detalle">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!ordenes.length && (
                    <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">
                      No hay órdenes de trabajo{estado ? ' en este estado' : ''} — crea una con “Nueva OT”
                    </td></tr>
                  )}
                  {sort.sorted.length > 0 && (
                    <tr className="border-t-2 font-semibold bg-muted/30">
                      <td className="py-2 pr-3" colSpan={6}>Totales ({sort.sorted.length})</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(sort.sorted.reduce((a, r) => a + Number(r.unidades_plan), 0), 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(sort.sorted.reduce((a, r) => a + Number(r.unidades_producidas), 0), 0)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sort.sorted.reduce((a, r) => a + totalCosto(r), 0))}</td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {nuevaAbierta && <NuevaOtModal onClose={() => setNuevaAbierta(false)} onCreada={(id) => { setNuevaAbierta(false); setDetalleId(id); invalidar(); }} />}
      {detalleId && <DetalleOtModal ordenId={detalleId} onClose={() => setDetalleId(null)} />}
    </div>
  );
}

// ============================ Nueva OT ============================
function NuevaOtModal({ onClose, onCreada }: { onClose: () => void; onCreada: (id: string) => void }) {
  const [tipo, setTipo] = useState<'interna' | 'maquila'>('interna');
  const [fecha, setFecha] = useState(() => fechaLocal());
  const [padreId, setPadreId] = useState('');
  const [bodegaMp, setBodegaMp] = useState('');
  const [bodegaPt, setBodegaPt] = useState('');
  const [maquilador, setMaquilador] = useState('');
  const [obs, setObs] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>({});

  const { data: padres = [] } = useQuery<Producto[]>({
    queryKey: ['prod-padres'],
    queryFn: () => inv.list<Producto>('productos', '?select=id,codigo,nombre,categoria_id&tiene_variantes=eq.true&deleted_at=is.null&order=codigo'),
  });
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const proveedores = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('proveedor')), [partners]);

  const { data: variantes = [] } = useQuery<VarianteOpt[]>({
    queryKey: ['prod-variantes', padreId], enabled: !!padreId,
    queryFn: () => inv.list<VarianteOpt>('productos', `?select=id,codigo,nombre&parent_id=eq.${padreId}&deleted_at=is.null&es_activo=eq.true&order=codigo`),
  });

  const crear = useMutation({
    mutationFn: () => prodRpc<{ orden_id: string; codigo: string }>('ot_crear', {
      _payload: {
        tipo, fecha, producto_padre_id: padreId, bodega_mp_id: bodegaMp, bodega_pt_id: bodegaPt,
        maquilador_id: tipo === 'maquila' ? maquilador : null, observaciones: obs || null,
        productos: variantes.filter((v) => toNum(cantidades[v.id] ?? '') > 0)
          .map((v) => ({ producto_id: v.id, cantidad: toNum(cantidades[v.id]) })),
      },
    }),
    onSuccess: (r) => { toast.success(`Orden ${r.codigo} creada (borrador)`); onCreada(r.orden_id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalUds = variantes.reduce((a, v) => a + toNum(cantidades[v.id] ?? ''), 0);
  const listo = padreId && bodegaMp && bodegaPt && totalUds > 0 && (tipo === 'interna' || maquilador);

  return (
    <Modal title="Nueva Orden de Trabajo" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          <Campo label="Tipo">
            <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as 'interna' | 'maquila')}>
              <option value="interna">Producción interna</option>
              <option value="maquila">Maquila (tercero)</option>
            </select>
          </Campo>
          <Campo label="Fecha">
            <input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Campo>
          <Campo label="Bodega materia prima">
            <select className={fieldCls} value={bodegaMp} onChange={(e) => setBodegaMp(e.target.value)}>
              <option value="">— Bodega MP —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Bodega producto terminado">
            <select className={fieldCls} value={bodegaPt} onChange={(e) => setBodegaPt(e.target.value)}>
              <option value="">— Bodega PT —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
        </div>

        {tipo === 'maquila' && (
          <Campo label="Maquilador (proveedor del servicio)" className="max-w-xl">
            <select className={fieldCls} value={maquilador} onChange={(e) => setMaquilador(e.target.value)}>
              <option value="">— Selecciona —</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
            </select>
          </Campo>
        )}

        <Campo label="Modelo a fabricar (producto padre con receta)" className="max-w-xl">
          <select className={fieldCls} value={padreId} onChange={(e) => { setPadreId(e.target.value); setCantidades({}); }}>
            <option value="">— Selecciona —</option>
            {padres.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
          </select>
        </Campo>

        {!!variantes.length && (
          <div className="max-h-72 overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-muted-foreground">
                  <th className="py-2 px-3 text-left">Variante (color/talla)</th>
                  <th className="py-2 px-3 text-right w-36">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {variantes.map((v) => (
                  <tr key={v.id} className="border-b last:border-0">
                    <td className="py-1.5 px-3">{v.codigo} · {v.nombre}</td>
                    <td className="py-1.5 px-3 text-right">
                      <input type="number" min="0" step="1" className={`${fieldCls} h-8 w-28 text-right ml-auto`}
                        value={cantidades[v.id] ?? ''} placeholder="0"
                        onChange={(e) => setCantidades((s) => ({ ...s, [v.id]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Campo label="Observaciones">
          <input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} />
        </Campo>

        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-sm text-muted-foreground">Total a fabricar: <span className="font-semibold text-foreground">{num(totalUds, 0)} uds</span></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button disabled={!listo} isLoading={crear.isPending} onClick={() => crear.mutate()}>Crear OT (borrador)</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ============================ Detalle OT ============================
function DetalleOtModal({ ordenId, onClose }: { ordenId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [entradaAbierta, setEntradaAbierta] = useState(false);
  const [maquilaAbierta, setMaquilaAbierta] = useState(false);

  const { data: ot, isFetching } = useQuery<OtDetalle>({
    queryKey: ['prod-ot-detalle', ordenId],
    queryFn: () => prodRpc<OtDetalle>('ot_obtener', { _orden_id: ordenId }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const invalidar = () => invalidateProduccion(qc);

  const confirmar = useMutation({
    mutationFn: () => prodRpc('ot_confirmar', { _orden_id: ordenId }),
    onSuccess: () => { toast.success('Orden confirmada: materia prima consumida'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: () => prodRpc('ot_anular', { _orden_id: ordenId }),
    onSuccess: () => { toast.success('Orden anulada'); invalidar(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => prodRpc<{ costo_merma: number }>('ot_cerrar', { _orden_id: ordenId }),
    onSuccess: (r) => {
      toast.success(Number(r.costo_merma) > 0 ? `Orden cerrada — merma ${money(r.costo_merma)}` : 'Orden cerrada sin merma');
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!ot) {
    return (
      <Modal title="Orden de Trabajo" onClose={onClose}>
        <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
        </div>
      </Modal>
    );
  }

  const costoTotal = Number(ot.costo_mp) + Number(ot.costo_mo) + Number(ot.costo_cif) + Number(ot.costo_maquila);

  return (
    <Modal title={`${ot.codigo} · ${ot.producto_padre}`} onClose={onClose} wide>
      {dlg.ui}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_CLS[ot.estado] ?? ''}`}>{ESTADO_LABEL[ot.estado] ?? ot.estado}</span>
          <span className="capitalize">{ot.tipo}</span>
          {ot.maquilador && <span className="text-muted-foreground">Maquilador: {ot.maquilador}</span>}
          <span className="text-muted-foreground">Fecha: {ot.fecha}</span>
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
          {[
            ['Unidades plan', num(ot.unidades_plan, 0)], ['Producidas', num(ot.unidades_producidas, 0)],
            ['Materia prima', money(ot.costo_mp)], ['MO + CIF', money(Number(ot.costo_mo) + Number(ot.costo_cif))],
            ['Maquila', money(ot.costo_maquila)], ['Costo total', money(costoTotal)],
          ].map(([l, v]) => (
            <div key={l as string} className="rounded-md border p-2">
              <div className="text-xs text-muted-foreground">{l}</div>
              <div className="font-semibold font-mono tabular-nums">{v}</div>
            </div>
          ))}
        </div>

        <div>
          <h4 className="text-sm font-semibold mb-1">Variantes a fabricar</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="py-1.5 pr-3 text-left">Variante</th>
                <th className="py-1.5 pr-3 text-right">Plan</th>
                <th className="py-1.5 pr-3 text-right">Producido</th>
                <th className="py-1.5 pr-3 text-right">MP unit</th>
                <th className="py-1.5 pr-3 text-right">MO unit</th>
                <th className="py-1.5 pr-3 text-right">CIF unit</th>
              </tr>
            </thead>
            <tbody>
              {ot.productos.map((p) => (
                <tr key={p.producto_id} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{p.codigo} · {p.nombre}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(p.cantidad_plan, 0)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(p.cantidad_producida, 0)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(p.mp_unit, 4)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(p.mo_unit)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(p.cif_unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h4 className="text-sm font-semibold mb-1">Consumo de materia prima</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="py-1.5 pr-3 text-left">Material</th>
                <th className="py-1.5 pr-3 text-left">Unidad</th>
                <th className="py-1.5 pr-3 text-right">Plan</th>
                <th className="py-1.5 pr-3 text-right">Consumido</th>
                <th className="py-1.5 pr-3 text-right">Costo unit</th>
                <th className="py-1.5 pr-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {ot.consumos.map((c) => (
                <tr key={c.material_id} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{c.codigo} · {c.nombre}</td>
                  <td className="py-1.5 pr-3">{c.unidad ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(c.cantidad_plan, 4)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(c.cantidad_consumida, 4)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(c.costo_unit, 4)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(Number(c.cantidad_consumida || c.cantidad_plan) * Number(c.costo_unit))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!!ot.entradas.length && (
          <div>
            <h4 className="text-sm font-semibold mb-1">Entradas de producto terminado</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-1.5 pr-3 text-left">Fecha</th>
                  <th className="py-1.5 pr-3 text-left">Producto</th>
                  <th className="py-1.5 pr-3 text-right">Cantidad</th>
                  <th className="py-1.5 pr-3 text-right">Costo unit</th>
                  <th className="py-1.5 pr-3 text-left">Ubicación</th>
                </tr>
              </thead>
              <tbody>
                {ot.entradas.map((e, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1.5 pr-3">{e.fecha}</td>
                    <td className="py-1.5 pr-3">{e.producto}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(e.cantidad, 0)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(e.costo_unit, 4)}</td>
                    <td className="py-1.5 pr-3">{e.ubicacion ?? 'General'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!!ot.costos.length && (
          <div>
            <h4 className="text-sm font-semibold mb-1">Costos aplicados (maquila)</h4>
            <table className="w-full max-w-xl text-sm">
              <tbody>
                {ot.costos.map((c, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1.5 pr-3">{c.fecha}</td>
                    <td className="py-1.5 pr-3 capitalize">{c.tipo}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground">{c.referencia ?? ''}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(c.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {ot.costo_merma > 0 && (
          <div className="text-sm rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2">
            Merma liquidada al cierre: <span className="font-semibold">{money(ot.costo_merma)}</span>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
          {ot.estado === 'borrador' && (
            <>
              <Button variant="outline" onClick={async () => { if (await dlg.confirmar('¿Anular esta orden?', { peligro: true })) anular.mutate(); }} isLoading={anular.isPending}>
                <XCircle className="h-4 w-4 mr-1" /> Anular
              </Button>
              <Button onClick={() => confirmar.mutate()} isLoading={confirmar.isPending}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Confirmar (consumir MP)
              </Button>
            </>
          )}
          {ot.estado === 'en_proceso' && (
            <>
              {ot.tipo === 'maquila' && (
                <Button variant="outline" onClick={() => setMaquilaAbierta(true)}>
                  <DollarSign className="h-4 w-4 mr-1" /> Costo maquila
                </Button>
              )}
              <Button onClick={() => setEntradaAbierta(true)}>
                <PackageCheck className="h-4 w-4 mr-1" /> Registrar entrada PT
              </Button>
            </>
          )}
          {(ot.estado === 'en_proceso' || ot.estado === 'terminada') && (
            <Button variant="outline" isLoading={cerrar.isPending}
              onClick={async () => { if (await dlg.confirmar('¿Continuar?', { detalle: 'Cerrar la orden liquida el WIP restante a merma.' })) cerrar.mutate(); }}>
              Cerrar orden
            </Button>
          )}
        </div>
      </div>

      {entradaAbierta && <EntradaPtModal ot={ot} onClose={() => setEntradaAbierta(false)} onOk={() => { setEntradaAbierta(false); invalidar(); }} />}
      {maquilaAbierta && <MaquilaCostoModal ordenId={ot.id} onClose={() => setMaquilaAbierta(false)} onOk={() => { setMaquilaAbierta(false); invalidar(); }} />}
    </Modal>
  );
}

// ---- Entrada parcial de PT ----
function EntradaPtModal({ ot, onClose, onOk }: { ot: OtDetalle; onClose: () => void; onOk: () => void }) {
  const pendientes = ot.productos.filter((p) => Number(p.cantidad_producida) < Number(p.cantidad_plan));
  const [cant, setCant] = useState<Record<string, string>>({});
  const [ubic, setUbic] = useState('');

  const { data: ubicaciones = [] } = useQuery<UbicacionOpt[]>({
    queryKey: ['inv-ubicaciones', ot.bodega_pt_id],
    queryFn: () => ubicacionesList(ot.bodega_pt_id),
  });

  const registrar = useMutation({
    mutationFn: () => prodRpc('ot_entrada', {
      _orden_id: ot.id,
      _lineas: pendientes.filter((p) => toNum(cant[p.producto_id] ?? '') > 0)
        .map((p) => ({ producto_id: p.producto_id, cantidad: toNum(cant[p.producto_id]), ubicacion_id: ubic || null })),
    }),
    onSuccess: () => { toast.success('Entrada registrada'); onOk(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const total = pendientes.reduce((a, p) => a + toNum(cant[p.producto_id] ?? ''), 0);

  return (
    <Modal title={`Entrada de producto terminado · ${ot.codigo}`} onClose={onClose} wide>
      <div className="space-y-3">
        <Campo label="Ubicación destino (bin) — opcional, por defecto General" className="max-w-md">
          <select className={fieldCls} value={ubic} onChange={(e) => setUbic(e.target.value)}>
            <option value="">General (por defecto)</option>
            {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.codigo}</option>)}
          </select>
        </Campo>
        <div className="max-h-80 overflow-y-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-muted-foreground">
                <th className="py-2 px-3 text-left">Variante</th>
                <th className="py-2 px-3 text-right">Pendiente</th>
                <th className="py-2 px-3 text-right w-36">Entrada</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((p) => {
                const pend = Number(p.cantidad_plan) - Number(p.cantidad_producida);
                return (
                  <tr key={p.producto_id} className="border-b last:border-0">
                    <td className="py-1.5 px-3">{p.codigo} · {p.nombre}</td>
                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">{num(pend, 0)}</td>
                    <td className="py-1.5 px-3 text-right">
                      <input type="number" min="0" max={pend} step="1" className={`${fieldCls} h-8 w-28 text-right ml-auto`}
                        value={cant[p.producto_id] ?? ''} placeholder="0"
                        onChange={(e) => setCant((s) => ({ ...s, [p.producto_id]: e.target.value }))} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-sm text-muted-foreground">Total entrada: <span className="font-semibold text-foreground">{num(total, 0)} uds</span></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button disabled={total <= 0} isLoading={registrar.isPending} onClick={() => registrar.mutate()}>Registrar entrada</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---- Costo del servicio de maquila ----
function MaquilaCostoModal({ ordenId, onClose, onOk }: { ordenId: string; onClose: () => void; onOk: () => void }) {
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');

  const registrar = useMutation({
    mutationFn: () => prodRpc('ot_maquila_costo', { _orden_id: ordenId, _monto: toNum(monto), _referencia: referencia || null }),
    onSuccess: () => { toast.success('Costo de maquila aplicado a la orden'); onOk(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Registrar costo de maquila" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Monto del servicio (USD, sin IVA)">
          <input type="number" min="0" step="0.01" className={fieldCls} value={monto} onChange={(e) => setMonto(e.target.value)} />
        </Campo>
        <Campo label="Referencia (factura del maquilador)">
          <input className={fieldCls} value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="001-001-000000123" />
        </Campo>
        <p className="text-xs text-muted-foreground">
          El monto se capitaliza al costo de la orden (Productos en Proceso). La factura del maquilador
          sigue su flujo normal en Financiero → Gestión SRI.
        </p>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={toNum(monto) <= 0} isLoading={registrar.isPending} onClick={() => registrar.mutate()}>Aplicar costo</Button>
        </div>
      </div>
    </Modal>
  );
}
